import { Dispatch, SetStateAction, useLayoutEffect } from 'react';

import { HistoryContent } from '../types';

type WindowData =
  | {
      publishedScheduledContentWindow: { body: unknown }[];
    }
  | undefined;

interface UseWindowSnapParams<T extends HistoryContent> {
  liveVersion: T | undefined;
  windowData: WindowData;
  windowTarget: number | null;
  setWindowTarget: Dispatch<SetStateAction<number | null>>;
  setWindowItems: Dispatch<SetStateAction<T[]>>;
  setWindowOlderAnchor: Dispatch<SetStateAction<number | null>>;
  setWindowNewerAnchor: Dispatch<SetStateAction<number | null>>;
  setCurrentItemPublishUtc: Dispatch<SetStateAction<number | null>>;
}

// When window data arrives, replace windowItems with the new window, navigate to the target, and arm
// the older/newer cursor walkers so subsequent Prev/Next prefetch extends the window.
export const useWindowSnap = <T extends HistoryContent>({
  liveVersion,
  windowData,
  windowTarget,
  setWindowTarget,
  setWindowItems,
  setWindowOlderAnchor,
  setWindowNewerAnchor,
  setCurrentItemPublishUtc
}: UseWindowSnapParams<T>) => {
  useLayoutEffect(() => {
    if (!windowData?.publishedScheduledContentWindow || !windowTarget) return;
    const newItems = windowData.publishedScheduledContentWindow.map((c) => c.body as T);
    setWindowItems(newItems);
    const itemsBelowLive = newItems.filter(
      (i) => i.metadata.publishUtc != null && i.metadata.publishUtc < (liveVersion?.metadata.publishUtc ?? 0)
    );
    if (itemsBelowLive.length > 0) {
      const utcs = itemsBelowLive.map((i) => i.metadata.publishUtc!);
      setWindowOlderAnchor(Math.min(...utcs));
      setWindowNewerAnchor(Math.max(...utcs));
    }
    setCurrentItemPublishUtc(windowTarget);
    setWindowTarget(null);
  }, [
    windowData,
    windowTarget,
    liveVersion,
    setWindowItems,
    setWindowOlderAnchor,
    setWindowNewerAnchor,
    setCurrentItemPublishUtc,
    setWindowTarget
  ]);
};
